import { useEffect, useCallback } from 'react';
import i18n, { ensureEnLoaded } from '../i18n';

export type SupportedLang = 'mk' | 'en';

const STORAGE_KEY = 'predikt.lang';
const DEFAULT_LANG: SupportedLang = 'mk';

function isSupported(value: string | null | undefined): value is SupportedLang {
  return value === 'mk' || value === 'en';
}

function readLocalStorage(): SupportedLang | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return isSupported(raw) ? raw : null;
  } catch {
    // localStorage may throw in Safari private mode / disabled storage; treat as
    // "not stored" rather than crashing the app boot.
    return null;
  }
}

function writeLocalStorage(lang: SupportedLang): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // ignore — same reason as readLocalStorage above.
  }
}

/** Strip `?lang=` from the current URL via replaceState. Preserves all other query params. */
function stripLangFromUrl(): void {
  const url = new URL(window.location.href);
  if (url.searchParams.has('lang')) {
    url.searchParams.delete('lang');
    // D-17: replaceState only — language is not a navigable history step.
    window.history.replaceState(window.history.state, '', url.toString());
  }
}

/** Set `?lang=<value>` on the URL via replaceState (D-17). MK = no param (clean URL). */
function writeLangToUrl(lang: SupportedLang): void {
  const url = new URL(window.location.href);
  if (lang === 'mk') {
    url.searchParams.delete('lang');
  } else {
    url.searchParams.set('lang', lang);
  }
  window.history.replaceState(window.history.state, '', url.toString());
}

async function applyLang(resolved: SupportedLang): Promise<void> {
  try {
    // D-02: load EN bundle lazily before activating.
    if (resolved === 'en') {
      await ensureEnLoaded();
    }
    if (i18n.language !== resolved) {
      await i18n.changeLanguage(resolved);
    }
    document.documentElement.lang = resolved;
    writeLocalStorage(resolved);
  } catch (err) {
    // WR-001: surface async failures so EN-chunk-load errors aren't invisible.
    // The most plausible failure is `ensureEnLoaded()`'s dynamic
    // `import('./locales/en.json')` rejecting (chunk-load 404 after a deploy
    // that invalidates old hashed chunks, network blip, malformed JSON, CDN
    // miss). Without this log the rejection is swallowed by the `void IIFE`
    // callers and the user sees a silent no-op on click. Phase 2 minimum:
    // log to console; Phase 7 (telemetry) should pipe this to Sentry/similar.
    // Re-throw so any future error boundary or upstream awaiter can react —
    // the current `void IIFE` callers still swallow but the console.error
    // has already landed.
    console.error('[predikt] failed to apply language', resolved, err);
    throw err;
  }
}

// CR-001: module-level side-effect guard. The init effect runs once even if
// useLangSyncInit is accidentally mounted twice (or React StrictMode double-
// invokes the effect in dev). Belt-and-suspenders alongside the layout-only
// mount contract documented on `useLangSyncInit`.
let initStarted = false;

// WR-002: monotonic generation counter for setLang. Each invocation
// increments it; in-flight chains capture their `gen` and re-check it after
// each await. A stale chain (one whose generation was superseded by a later
// click) bails out before mutating i18n/document/localStorage. Prevents the
// rapid-double-click race during the first MK→EN transition where click 2's
// URL write can land while click 1's EN-chunk fetch is still resolving.
let setLangGen = 0;

/**
 * useLangSyncInit — D-16 implementation. Mount ONCE inside `<RootLayout/>`.
 *
 * Runs steps 1–7 on first mount only:
 *   1. Read `?lang=` from current URL.
 *   2. Validate against ('mk' | 'en') allowlist.
 *   3. If invalid, silently strip from URL via replaceState and fall through.
 *   4. Resolve: valid URL value > localStorage > MK default. (No Accept-Language — LOC-05.)
 *   5. Call i18n.changeLanguage(resolved) if it differs from i18n.language.
 *   6. Set document.documentElement.lang = resolved.
 *   7. Persist localStorage['predikt.lang'] = resolved.
 *
 * CR-001: separated from `useSetLang` so the side effect runs exactly once at
 * the layout root, while `setLang` remains reusable from any consumer
 * (e.g. `<LangToggle/>`) without remounting the reconciler.
 */
export function useLangSyncInit(): void {
  useEffect(() => {
    if (initStarted) return;
    initStarted = true;

    let cancelled = false;
    void (async () => {
      // Step 1: read URL.
      const url = new URL(window.location.href);
      const fromUrlRaw = url.searchParams.get('lang');

      // Step 2: validate.
      const fromUrl = isSupported(fromUrlRaw) ? fromUrlRaw : null;

      // Step 3: invalid value → strip from URL (silent recovery).
      if (fromUrlRaw !== null && fromUrl === null) {
        stripLangFromUrl();
      }

      // Step 4: resolve precedence: URL > localStorage > default. No Accept-Language.
      const fromStorage = readLocalStorage();
      const resolved: SupportedLang = fromUrl ?? fromStorage ?? DEFAULT_LANG;

      // Steps 5–7.
      if (!cancelled) {
        await applyLang(resolved);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
}

/**
 * useSetLang — returns the stable `setLang(next)` callback used by
 * `<LangToggle/>` (and any future consumer that needs to flip language).
 *
 * Writes `?lang=` to the URL via replaceState (D-17 — back button does NOT
 * undo a language toggle), then re-runs steps 4–7 with `next` as the resolved
 * value.
 *
 * WR-002: serialises rapid double-clicks via a module-level generation
 * counter. Each call captures `gen` and re-checks it after each await; a
 * stale chain (one whose generation was superseded) returns early before
 * mutating i18n/document/localStorage. The synchronous URL write happens
 * unconditionally because the URL must always reflect the latest user
 * intent — the asynchronous mutations downstream are the ones that need
 * serialising.
 */
export function useSetLang(): (next: SupportedLang) => void {
  return useCallback((next: SupportedLang) => {
    const gen = ++setLangGen;
    void (async () => {
      try {
        writeLangToUrl(next); // D-17: replaceState only.
        if (next === 'en') {
          await ensureEnLoaded();
        }
        if (gen !== setLangGen) return; // a newer click superseded this one
        if (i18n.language !== next) {
          await i18n.changeLanguage(next);
        }
        if (gen !== setLangGen) return;
        document.documentElement.lang = next;
        writeLocalStorage(next);
      } catch (err) {
        // WR-001 companion: log so a failed flip isn't invisible. applyLang's
        // try/catch only covers the init path; the setter path is now inlined
        // here (to thread `gen` checks between awaits) so it needs its own
        // logging surface.
        console.error('[predikt] setLang failed', next, err);
      }
    })();
  }, []);
}
